import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Calculator, CheckCircle2, Search } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Badge } from '../components/ui/Badge'
import { apiError, type IndustryTenant } from '../lib/industria'
import { type RecipeRow } from '../lib/producao'

const selectClass = 'h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60'

type Linha = {
  materialId: string; materialCode: string; materialName: string; unit: string
  required: boolean; qtyPerUnit: string
  need: number; saldo: number; reservado: number; disponivel: number; falta: number
  comporta: number | null
  fornecedor?: string | null; prazoDias?: number | null
}

type Simulacao = {
  recipeVersion: number; tenantName: string | null
  productSku: string | null; productName: string | null
  qty: number; linhas: Linha[]
  faltando: number; possivel: number
  gargalo: { materialCode: string; materialName: string } | null
  prazoMaximo: number
}

// "Para produzir 500 unidades disto, o que falta?" — a pergunta que se faz antes
// de assumir um pedido. Consulta pura: não cria ordem nem reserva nada.
export function IndustriaSimuladorPage() {
  const [tenantId, setTenantId] = useState('')
  const [productId, setProductId] = useState('')
  const [qty, setQty] = useState('500')
  const [consulta, setConsulta] = useState<{ productId: string; qty: number } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data: tenants = [] } = useQuery<IndustryTenant[]>({
    queryKey: ['industria', 'tenants'],
    queryFn: async () => (await api.get('/materials/tenants')).data,
  })

  // Só produto com ficha ATIVA pode ser simulado — é a ficha que diz o que entra.
  const { data: recipes = [] } = useQuery<RecipeRow[]>({
    queryKey: ['producao', 'recipes', tenantId, 'active'],
    queryFn: async () => (await api.get('/product-recipes', {
      params: { tenantId: tenantId || undefined, status: 'active' },
    })).data,
  })

  const { data: sim, isFetching } = useQuery<Simulacao>({
    queryKey: ['simulador', consulta?.productId, consulta?.qty],
    queryFn: async () => (await api.get('/production-simulate', {
      params: { productId: consulta!.productId, qty: consulta!.qty },
    })).data,
    enabled: !!consulta,
  })

  function simular() {
    setError(null)
    const n = Number(qty)
    if (!productId || !n || n < 1) {
      setError('Escolha o produto e uma quantidade válida.')
      return
    }
    setConsulta({ productId, qty: n })
  }

  const daParaFazer = sim ? sim.possivel >= sim.qty : false

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Dá para produzir?</h2>
        <p className="text-sm text-gray-500">
          O que falta para fabricar uma quantidade — sem abrir ordem nem reservar nada
        </p>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="w-56">
            <label className="mb-1 block text-sm font-medium text-gray-700">Marca</label>
            <select
              className={selectClass}
              value={tenantId}
              onChange={e => { setTenantId(e.target.value); setProductId(''); setConsulta(null) }}
            >
              <option value="">Todas</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="min-w-[260px] flex-1">
            <label className="mb-1 block text-sm font-medium text-gray-700">Produto</label>
            <select
              className={selectClass}
              value={productId}
              onChange={e => { setProductId(e.target.value); setConsulta(null) }}
            >
              <option value="">Selecione...</option>
              {recipes.map(r => (
                <option key={r.id} value={r.productId}>
                  {r.productSku} — {r.productName} (ficha v{r.version})
                </option>
              ))}
            </select>
          </div>
          <div className="w-36">
            <Input
              label="Quantidade"
              type="number"
              min={1}
              value={qty}
              onChange={e => { setQty(e.target.value); setConsulta(null) }}
              onKeyDown={e => { if (e.key === 'Enter') simular() }}
            />
          </div>
          <Button onClick={simular} loading={isFetching}>
            <Search className="h-4 w-4" /> Consultar
          </Button>
        </CardContent>
      </Card>

      {error && <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}

      {recipes.length === 0 && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Nenhum produto com ficha técnica ativa. Sem ficha não há o que calcular —
          monte uma em <Link to="/industria/fichas" className="underline">Fichas técnicas</Link>.
        </p>
      )}

      {sim && (
        <>
          <Card className={daParaFazer ? 'border-green-200' : 'border-amber-200'}>
            <CardContent className="flex flex-wrap items-center gap-4">
              <div className={`rounded-xl p-2.5 ${daParaFazer ? 'bg-green-50' : 'bg-amber-50'}`}>
                {daParaFazer
                  ? <CheckCircle2 className="h-6 w-6 text-green-600" />
                  : <AlertTriangle className="h-6 w-6 text-amber-600" />}
              </div>
              <div className="flex-1">
                <p className="text-base font-semibold text-gray-900">
                  {daParaFazer
                    ? `Dá para produzir as ${sim.qty.toLocaleString('pt-BR')} unidades.`
                    : `Falta material para ${sim.qty.toLocaleString('pt-BR')} unidades.`}
                </p>
                <p className="text-sm text-gray-600">
                  Com o saldo de hoje dá para fazer{' '}
                  <b className="font-mono">{sim.possivel.toLocaleString('pt-BR')}</b> unidades
                  {sim.gargalo && !daParaFazer && (
                    <> — o gargalo é <b>{sim.gargalo.materialName}</b></>
                  )}.
                  {sim.prazoMaximo > 0 && (
                    <> O fornecedor mais demorado leva <b>{sim.prazoMaximo} dias</b>.</>
                  )}
                </p>
              </div>
              <div className="text-right text-sm text-gray-500">
                {sim.productName}<br />
                <span className="font-mono text-xs">
                  {sim.productSku} · ficha v{sim.recipeVersion} · {sim.tenantName}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <span className="text-sm font-semibold text-gray-900">
                Componente a componente
              </span>
              <span className="text-xs text-gray-500">
                disponível = saldo do dono − reservado por outras ordens
              </span>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-medium">Material</th>
                      <th className="px-4 py-2.5 text-right font-medium">Precisa</th>
                      <th className="px-4 py-2.5 text-right font-medium">Saldo</th>
                      <th className="px-4 py-2.5 text-right font-medium">Reservado</th>
                      <th className="px-4 py-2.5 text-right font-medium">Disponível</th>
                      <th className="px-4 py-2.5 text-right font-medium">Falta</th>
                      <th className="px-4 py-2.5 text-right font-medium">Comporta</th>
                      {sim.linhas.some(l => l.fornecedor !== undefined) && (
                        <th className="px-4 py-2.5 text-left font-medium">Fornecedor</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {sim.linhas.map(l => (
                      <tr key={l.materialId} className={l.falta > 0 ? 'bg-amber-50/40' : undefined}>
                        <td className="px-4 py-2.5">
                          <span className="text-gray-900">{l.materialName}</span>
                          <span className="block font-mono text-xs text-gray-400">
                            {l.materialCode}{!l.required ? ' · opcional' : ''}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                          {l.need.toLocaleString('pt-BR')}
                          <span className="ml-1 text-xs text-gray-400">{l.unit}</span>
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono text-gray-600">
                          {l.saldo.toLocaleString('pt-BR')}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono text-gray-500">
                          {l.reservado ? `−${l.reservado.toLocaleString('pt-BR')}` : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                          {l.disponivel.toLocaleString('pt-BR')}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          {l.falta > 0 ? (
                            <Badge variant="warning">{l.falta.toLocaleString('pt-BR')}</Badge>
                          ) : (
                            <span className="text-green-600">ok</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono text-xs text-gray-500">
                          {l.comporta == null ? '—' : `${l.comporta.toLocaleString('pt-BR')} un`}
                        </td>
                        {l.fornecedor !== undefined && (
                          <td className="px-4 py-2.5 text-gray-600">
                            {l.fornecedor ?? '—'}
                            {l.prazoDias ? (
                              <span className="block text-xs text-gray-400">{l.prazoDias} dias</span>
                            ) : null}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {sim.faltando > 0 && (
            <p className="text-sm text-gray-600">
              Para comprar o que falta com a conta da demanda real (que soma todas as ordens
              planejadas, não só esta simulação), vá em{' '}
              <Link to="/industria/compras" className="text-blue-600 hover:underline">Compras</Link>.
            </p>
          )}
        </>
      )}
    </div>
  )
}
